/**
 * Line selection hook for all document viewers.
 *
 * Supports:
 *   Click          → select one line (clears previous)
 *   Ctrl/Cmd+Click → toggle one line without affecting others
 *   Shift+Click    → range select from anchor to clicked line
 *   Ctrl+Shift     → add range to existing selection
 *   Ctrl+A         → select all
 *   Escape         → clear selection
 *   Ctrl+C         → copy selected lines to clipboard
 */

import {useCallback, useEffect, useRef, useState} from 'react';
import {DocLine} from './types';

export interface LineSelectionState {
    selectedLines: Set<number>;
    onLineClick: (index: number, e: React.MouseEvent) => void;
    clearSelection: () => void;
    selectAll: () => void;
    selectionCount: number;
    copySelection: () => void;
}

export function useLineSelection(lines: DocLine[]): LineSelectionState {
    const [selectedLines, setSelectedLines] = useState<Set<number>>(new Set());
    const anchorRef = useRef<number | null>(null);

    const onLineClick = useCallback((index: number, e: React.MouseEvent) => {
        const isMeta = e.metaKey || e.ctrlKey;
        const isShift = e.shiftKey;

        setSelectedLines(prev => {
            if (isShift && anchorRef.current !== null) {
                // Range select from anchor to clicked line
                const start = Math.min(anchorRef.current, index);
                const end = Math.max(anchorRef.current, index);
                const range = new Set<number>();
                for (let i = start; i <= end; i++) range.add(i);

                if (isMeta) {
                    // Ctrl+Shift: add range to existing selection
                    const next = new Set(prev);
                    for (const i of range) next.add(i);
                    return next;
                }
                // Shift only: replace selection with range
                return range;
            }

            if (isMeta) {
                // Ctrl/Cmd: toggle single line
                const next = new Set(prev);
                if (next.has(index)) {
                    next.delete(index);
                } else {
                    next.add(index);
                }
                anchorRef.current = index;
                return next;
            }

            // Plain click: select only this line
            anchorRef.current = index;
            return new Set([index]);
        });
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedLines(new Set());
        anchorRef.current = null;
    }, []);

    const selectAll = useCallback(() => {
        const all = new Set<number>();
        for (let i = 0; i < lines.length; i++) all.add(i);
        setSelectedLines(all);
    }, [lines.length]);

    const getSelectedText = useCallback(() => {
        return Array.from(selectedLines)
            .sort((a, b) => a - b)
            .map(i => lines[i]?.text || '')
            .join('\n');
    }, [selectedLines, lines]);

    const copySelection = useCallback(() => {
        const text = getSelectedText();
        if (text) navigator.clipboard.writeText(text);
    }, [getSelectedText]);

    // Keyboard shortcuts — only when focus is NOT in an input/textarea/contentEditable,
    // so Ctrl+A in the PDF search bar (or any other input) behaves normally.
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement;
            const inInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

            if (e.key === 'Escape') {
                clearSelection();
                return;
            }

            // Don't intercept meta-key combos when focus is in a text field
            if (inInput) return;

            const isMeta = e.metaKey || e.ctrlKey;
            if (isMeta && e.key === 'a') {
                if (selectedLines.size > 0) {
                    e.preventDefault();
                    selectAll();
                }
                return;
            }
            if (isMeta && e.key === 'c' && selectedLines.size > 0) {
                copySelection();
            }
        };

        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [selectedLines, clearSelection, selectAll, copySelection]);

    return {
        selectedLines,
        onLineClick,
        clearSelection,
        selectAll,
        selectionCount: selectedLines.size,
        copySelection,
    };
}
